/* 全系统日期显示：美国常规格式 MM/DD/YYYY · 24 小时制。数据仍按 ISO 存储，仅改显示。 */
const pad = (n) => String(n).padStart(2, "0");

/* ---------- 时区化的「怎么读这个时间戳」（2026-10-29） ----------
   同一份带时区的时间戳，在不同时区看会落在不同的日历日。原来这里一律用
   浏览器本机 getter（`new Date(s).getFullYear()` 等），于是同一条截止时间，
   中国同事和美国同事打开会差一天 —— 这正是用户反复反馈的那类「两边看到的日子不一样」。

   现在把「取年月日时分」抽成工厂，两类实例各自明确：
     · makeDateFmt("")           → 仍读浏览器本机时区。HRMS（中国主体）、财务、
                                   行政审批等模块沿用旧口径，行为逐字不变。
     · makeDateFmt("America/…")  → 固定按该时区取。设计部全模块统一走这个
                                   （见 lib/usDate.js），中英文界面下输出也完全相同。

   注意区分两种值：
     · 纯日期 `2026-10-29` / 无时区的 `2026-10-29T10:00`：业务日历日或墙上时间，
       按字面呈现，**不做任何时区换算**（换算了反而会把用户选的日期挪一天）；
     · 带 Z / 偏移的时间戳：真实瞬间，按目标时区换算后再取日历日。 */
const TZ_FORMATTERS = new Map();

const tzFormatter = (timeZone) => {
  if (!timeZone) return null;
  if (TZ_FORMATTERS.has(timeZone)) return TZ_FORMATTERS.get(timeZone);
  let f = null;
  try {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone, hourCycle: "h23",
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { f = null; } // 非法时区名 → 退回浏览器本地，不让页面白屏
  TZ_FORMATTERS.set(timeZone, f);
  return f;
};

/** 用指定 formatter 取「该时区墙上的年月日时分」 */
const partsIn = (fmt, date) => {
  const p = {};
  for (const { type, value } of fmt.formatToParts(date)) p[type] = value;
  // hourCycle=h23 在个别实现里仍可能给出 "24"，取模兜底
  return { y: +p.year, m: +p.month, d: +p.day, hh: +p.hour % 24, mm: +p.minute };
};

const localParts = (date) => ({
  y: date.getFullYear(), m: date.getMonth() + 1, d: date.getDate(), hh: date.getHours(), mm: date.getMinutes(),
});

/**
 * 生成一套按 timeZone 取值的日期工具。
 * timeZone 为空 = 浏览器本机时区（旧口径）；传 IANA 名 = 固定该时区。
 */
export const makeDateFmt = (timeZone = "") => {
  const fmt = tzFormatter(timeZone);
  const view = (date) => (fmt ? partsIn(fmt, date) : localParts(date));

  const parse = (v) => {
    if (v == null || v === "") return null;
    if (v instanceof Date) return isNaN(v) ? null : { ...view(v), hasTime: true };
    const s = String(v).trim();
    // 纯日期 / 无时区的本地时间：按字面解析，不做时区换算
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/);
    if (m) {
      if (m[6] && m[4] != null) {
        const d = new Date(s);
        if (!isNaN(d)) return { ...view(d), hasTime: true };
      }
      return { y: +m[1], m: +m[2], d: +m[3], hh: m[4] != null ? +m[4] : 0, mm: m[5] != null ? +m[5] : 0, hasTime: m[4] != null };
    }
    const d = new Date(s);
    if (isNaN(d)) return null;
    return { ...view(d), hasTime: /\d{1,2}:\d{2}/.test(s) };
  };

  const raw = (v, fallback) => (v == null || v === "" ? fallback : fallback !== "" ? fallback : String(v));

  /**
   * 日期输入框的 YYYY-MM-DD 初始值。
   * 带时区的 ISO 瞬间必须换算成**目标时区的日历日**，不能直接截 UTC 字符串，
   * 否则同一截止时间在两个时区会被预填成前一天或后一天。
   */
  const dateInputValue = (v) => {
    const p = parse(v);
    return p ? `${p.y}-${pad(p.m)}-${pad(p.d)}` : "";
  };

  /** MM/DD/YYYY；空 → ""；无法解析的文本 → 原样返回（避免丢信息） */
  const fmtD = (v, fallback = "") => {
    const p = parse(v);
    return p ? `${pad(p.m)}/${pad(p.d)}/${p.y}` : raw(v, fallback);
  };

  /** MM/DD/YYYY HH:mm（24h）；无时间部分时只显示日期 */
  const fmtDT = (v, fallback = "") => {
    const p = parse(v);
    if (!p) return raw(v, fallback);
    return p.hasTime ? `${pad(p.m)}/${pad(p.d)}/${p.y} ${pad(p.hh)}:${pad(p.mm)}` : `${pad(p.m)}/${pad(p.d)}/${p.y}`;
  };

  /** MM/DD */
  const fmtMD = (v, fallback = "") => {
    const p = parse(v);
    return p ? `${pad(p.m)}/${pad(p.d)}` : raw(v, fallback);
  };

  /** HH:mm（24h） */
  const fmtT = (v, fallback = "") => {
    const p = parse(v);
    return p && p.hasTime ? `${pad(p.hh)}:${pad(p.mm)}` : fallback;
  };

  /** 把自由文本（历史记录/通知）内嵌的 ISO 日期就地换成美式格式；兼容老数据 */
  const ISO_IN_TEXT = /\b(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?\b/g;
  const fmtText = (s) => (typeof s === "string" && /\d{4}-\d{2}-\d{2}/.test(s) ? s.replace(ISO_IN_TEXT, (t) => fmtDT(t)) : s);

  /** 日期区间 MM/DD/YYYY – MM/DD/YYYY（同年时右侧省略年份 → MM/DD） */
  const fmtRange = (a, b) => {
    const pa = parse(a), pb = parse(b);
    if (!pa) return fmtD(b);
    if (!pb) return fmtD(a);
    return pa.y === pb.y ? `${pad(pa.m)}/${pad(pa.d)} – ${pad(pb.m)}/${pad(pb.d)}/${pb.y}` : `${fmtD(a)} – ${fmtD(b)}`;
  };

  return { parse, dateInputValue, fmtD, fmtDT, fmtMD, fmtT, fmtText, fmtRange };
};

/* ---------- 默认实例：浏览器本机时区（非设计模块的既有口径） ----------
   2026-10-29 起设计部改用 lib/usDate.js 的固定美国时区实例；
   这里的导出保持原行为，HRMS / 财务 / 行政审批等页面不受影响。 */
const local = makeDateFmt("");

export const dateInputValue = local.dateInputValue;
export const fmtD = local.fmtD;
export const fmtDT = local.fmtDT;
export const fmtMD = local.fmtMD;
export const fmtT = local.fmtT;
export const fmtText = local.fmtText;
export const fmtRange = local.fmtRange;

/**
 * 把任意日期值归一成 `YYYY-MM-DD` 日历日（2026-10-29 主线「待办分组按查看者日」引入）。
 *
 * 语义与 `dateInputValue` 完全一致，区别只是把时区当**调用参数**传进来：
 *   · 纯日期 / 无时区墙上时间 → 按字面返回，不做换算；
 *   · 带 Z / 偏移的时间戳 → 先换算到 `timeZone`（缺省 = 浏览器本机）再取日历日。
 *
 * 合并说明（2026-10-29）：主线原先自己写了一份 Intl 实现，与本分支抽出的
 * `makeDateFmt(timeZone)` 是同一套逻辑。这里统一委托给工厂，避免两份实现日后
 * 各自漂移 —— 设计部固定洛杉矶口径走 lib/usDate.js，其他模块继续读浏览器本机，
 * 但「怎么从一个时间戳取出日历日」全局只有一份代码。
 */
export const dateKeyForViewer = (value, timeZone) => makeDateFmt(timeZone || "").dateInputValue(value);

/**
 * 待办是否「今天到期」。沿用待办模块既有分组规则（2026-10-29 合并自主线）：
 *   · 催收流程 ready 档：按**查看者日历日**比较（用户在哪个时区就该在哪天看到）；
 *   · 设计类待办（design_deliverable / design_change）：固定洛杉矶日历日，
 *     与设计部统一后的时区口径一致；
 *   · 其余待办：保持历史口径（直接截字符串前 10 位），不动既有行为。
 */
export const todoIsDueToday = (todo, now = new Date(), timeZone) => {
  if (!todo?.dueDate) return false;
  const isReadyCollection = todo.origin === "collection_workflow" && todo.collectionLevel === "ready";
  const isDesignTodo = ["design_deliverable", "design_change"].includes(todo.origin);
  const dueDate = isReadyCollection ? dateKeyForViewer(todo.dueDate, timeZone) : String(todo.dueDate).slice(0, 10);
  const today = dateKeyForViewer(now, isDesignTodo ? "America/Los_Angeles" : (isReadyCollection ? timeZone : undefined));
  return !!dueDate && dueDate === today;
};

// 自然月加减（月末自动截断），输入/输出均为 YYYY-MM-DD
export const addMonths = (dstr, m) => {
  if (!dstr || !/^\d{4}-\d{2}-\d{2}/.test(dstr)) return "";
  const [y0, m0, d0] = dstr.slice(0, 10).split("-").map(Number);
  const total = m0 - 1 + m;
  const y = y0 + Math.floor(total / 12), mo = ((total % 12) + 12) % 12;
  const last = new Date(Date.UTC(y, mo + 1, 0)).getUTCDate();
  return `${y}-${String(mo + 1).padStart(2, "0")}-${String(Math.min(d0, last)).padStart(2, "0")}`;
};

/** 指定时区的「今天」日期字符串（YYYY-MM-DD）。取不到时区时退回浏览器本地。 */
export const todayIn = (timeZone) => {
  const fmt = tzFormatter(timeZone);
  if (!fmt) {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
  const p = partsIn(fmt, new Date());
  return `${p.y}-${pad(p.m)}-${pad(p.d)}`;
};

/**
 * YYYY-MM-DD（指定时区的日历日）→ 该时区当天 00:00 的 UTC 瞬间。
 *
 * 为什么不能用 Date.UTC / 浏览器本地零点：
 *   · `new Date(Date.UTC(y,m,d))` 得到的是 **UTC** 零点，洛杉矶看是前一天 16/17 点，
 *     回看会整体少一天（就是 2026-10-27 线上「改期少顺延 1 天」那类偏差的来源）；
 *   · 浏览器本地零点又跟着查看者时区跑，中国同事提交的日期在美国落库后偏一天。
 * 这里用「墙上时间 → 反推偏移」迭代两次收敛，夏令时切换日也成立。
 */
export const dayStartIsoIn = (ymd, timeZone) => {
  const m = String(ymd || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  const fmt = tzFormatter(timeZone);
  if (!fmt) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0).toISOString();
  const wall = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
  let guess = wall;
  for (let i = 0; i < 3; i += 1) {
    const p = partsIn(fmt, new Date(guess));
    const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm);
    const offset = asUtc - guess;          // 该时区在这一刻的偏移
    const next = wall - offset;
    if (next === guess) break;
    guess = next;
  }
  return new Date(guess).toISOString();
};

/* ---------- 「提交者时区换算」的遗留 helper（已废止，仅保留导出做兼容） ----------
   2026-10-22 曾按「中国同事选 10/24 → 美国同事看到 10/23」的口径落库：
   前端提交本机时区 + 本机零点瞬间，后端按提交者时区解释这一天。
   2026-10-29 用户明确改口径：「全部统一按照美国时间走，显示-输入都一样」，
   设计部改用 lib/usDate.js 的 usDayStartIso（固定美国日历日），下面两个函数
   不再被设计部调用；保留导出是为了别的分支/旧代码合并时不至于 import 失败。 */
export const localTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { return ""; }
};

export const dayStartIso = (ymd) => {
  const m = String(ymd || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0).toISOString();
};
